import { Component } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { SelectionChangedEvent } from 'devextreme/ui/lookup';
import { GenericCrudAddComponent } from '../../../../../libs/components/generic-crud/generic-crud-add.component';
import { ECheckpointDataType, TId, WorkProcedureStep } from '../../../../../models';
import { merge, of } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  applyCheckpointValuesFromLookupItem,
  normalizeCheckpointValueList,
  stepHasCheckpoint,
  syncDefaultValuesControlWithChoices,
  syncRequiredControlWithCheckpoint,
} from '../work-procedure-step-default-values';

@Component({
  selector: 'app-steps-add',
  templateUrl: './steps-add.component.html',
  styleUrl: './steps-add.component.sass',
  standalone: false,
})
export class StepsAddComponent extends GenericCrudAddComponent<WorkProcedureStep> {

  checkpointsRes = this.api.resources.checkpoints;

  /** Allowed default values = `WorkProcedureStep.checkpointValues` (synced from lookup row when available). */
  defaultValuesChoices: string[] = [];
  currentCheckpointType:number = 0;

  protected override async preFetch(): Promise<void> {
    this.resource = this.api.resources.product.workProceduresSteps(this.initialFormValues.parentId as TId);
  }

  protected override onInitExtendActions(): void {
    const checkpointValuesCtrl = this.form!.get('checkpointValues')!;
    const defaultValuesCtrl = this.form!.get('defaultValues')!;
    merge(of(checkpointValuesCtrl.value), checkpointValuesCtrl.valueChanges)
      .pipe(
        takeUntil(this.destroy$),
        map(v => normalizeCheckpointValueList(v as string[] | null | undefined)),
      )
      .subscribe(choices => {
        this.defaultValuesChoices = choices;
        syncDefaultValuesControlWithChoices(defaultValuesCtrl, choices);
        this.cdr.markForCheck();
      });

    syncRequiredControlWithCheckpoint(this.form!, this.destroy$, this.cdr);
  }

  onCheckpointSelectionChanged(e: SelectionChangedEvent): void {
    const cvCtrl = this.form?.get('checkpointValues');
    if (!cvCtrl) {
      return;
    }
    this.currentCheckpointType = e.selectedItem.dataTypeId;
    applyCheckpointValuesFromLookupItem(cvCtrl, e.selectedItem);
  }

  initFormGroup(): FormGroup {

    return this.fb.group({
      checkpointId: [null],
      checkpointValues: [[] as string[]],
      order: [],
      instructions: [''],
      instructionsValue: [null as number | null],
      defaultValues: [[] as string[]],
      required: [false],

      defaultTextValue: [''],
      defaultLongTextValue: [''],
      defaultNumericValue: [null as number | null],
      defaultDateValue: [null as Date | null],
      defaultTimeValue: [null as Date | null],
      defaultBooleanValue: [false],
    });
  }

  protected override async mapFormEntity(): Promise<Partial<WorkProcedureStep>> {
    const v = this.form!.value as Record<string, unknown>;
    let arr: string[] = [];

    if (this.isSelectionType) {
      let defaultValues = v['defaultValues'];

      if (defaultValues != null && !Array.isArray(defaultValues)) {
        defaultValues = [defaultValues];
      }

      arr = Array.isArray(defaultValues)
        ? (defaultValues as unknown[])
          .filter(x => x != null && x !== '')
          .map(x => String(x))
        : [];

    } else if (this.isTextType) {
      arr = v['defaultTextValue']
        ? [String(v['defaultTextValue'])]
        : [];

    } else if (this.isLongTextType) {
      arr = v['defaultLongTextValue']
        ? [String(v['defaultLongTextValue'])]
        : [];

    } else if (this.isNumericType) {
      arr =
        v['defaultNumericValue'] !== null &&
        v['defaultNumericValue'] !== undefined &&
        v['defaultNumericValue'] !== ''
          ? [String(v['defaultNumericValue'])]
          : [];

    } else if (this.isDateType) {
      arr = v['defaultDateValue']
        ? [String(v['defaultDateValue'])]
        : [];

    } else if (this.isTimeType) {
      arr = v['defaultTimeValue']
        ? [String(v['defaultTimeValue'])]
        : [];

    } else if (this.isBooleanType) {
      arr = [String(!!v['defaultBooleanValue'])];
    }

    const instructionsValue = v['instructionsValue'];
    const checkpointId = (v['checkpointId'] as TId | null | undefined) ?? undefined;
    return {
      checkpointId,
      required: stepHasCheckpoint(checkpointId) && !!v['required'],
      instructions: String(v['instructions'] ?? ''),
      instructionsValue:
        instructionsValue == null || instructionsValue === ''
          ? null
          : Number(instructionsValue),
      defaultValues: arr,
    };
  }


  get isBooleanType(): boolean {
    return this.currentCheckpointType === ECheckpointDataType.Boolean;
  }

  get isTextType(): boolean {
    return (this.currentCheckpointType === ECheckpointDataType.Text || this.currentCheckpointType === ECheckpointDataType.Sensor);
  }

  get isLongTextType(): boolean {
    return this.currentCheckpointType === ECheckpointDataType.LongText;
  }

  get isNumericType(): boolean {
    return this.currentCheckpointType === ECheckpointDataType.Numeric;
  }

  get isDateType(): boolean {
    return this.currentCheckpointType === ECheckpointDataType.Date;
  }

  get isTimeType(): boolean {
    return this.currentCheckpointType === ECheckpointDataType.Time;
  }

  get isSelectionType(): boolean {
    return (this.currentCheckpointType === ECheckpointDataType.UniqueSelection || this.currentCheckpointType === ECheckpointDataType.MultipleSelection) ;
  }
}
